import { CustomerFormData } from '@/customers/application/types/CustomerTypes';
import { FormikHelpers } from 'formik';
import { useCreateCustomer } from '@/customers/application/adapters/createCustomer';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '@/common/presentation/SnackbarProvider';
import { useUpdateCustomer } from '@/customers/application/adapters/updateCustomer';
import { useFeatureFlag } from '@/common/presentation/hooks';
import { FEATURE_FLAGS } from '@/common/domain/constants/featureFlags';

export function useCustomerForm(
  isNew: boolean,
  customerId: number,
  onClose?: () => void,
  onSuccess?: (customer: { id: number; companyName: string }) => void
) {
  const redirectToCustomerDetailsAfterAddEnabled = useFeatureFlag(FEATURE_FLAGS.CUSTOMERS_ADD_NEW_SHOW_DETAILS);
  const stayOnTabAfterEditEnabled = useFeatureFlag(FEATURE_FLAGS.CUSTOMERS_RESOURCE_ASSIGNMENT_STAY_ON_TAB);

  const navigate = useNavigate();
  const { handleCreateCustomer, error: createError } = useCreateCustomer();
  const { handleUpdateCustomer, error: updateError, loading: updateLoading } = useUpdateCustomer(customerId);
  const { showSnackbar, showErrorWithDetails } = useSnackbar();

  const handleSubmit = async (values: CustomerFormData, { setSubmitting, resetForm }: FormikHelpers<CustomerFormData>) => {
    let redirectToDetailsPage = false;

    try {
      showSnackbar('Info', 'Saving customer...', 'info');
      if (isNew) {
        const customer = await handleCreateCustomer(values);
        showSnackbar('Success', 'Customer created successfully', 'success');

        // Call onSuccess callback if provided (for jobs/jobsites integration)
        if (onSuccess && customer?.id && customer?.companyName) {
          onSuccess({
            id: customer.id,
            companyName: customer.companyName
          });
        }

        redirectToDetailsPage = Boolean(redirectToCustomerDetailsAfterAddEnabled && customer?.id && !onClose && !onSuccess);
        if (redirectToDetailsPage) {
          navigate(`/customers/${customer?.id}`);
          resetForm();
          return; // Early return to avoid the navigation logic below
        }
      } else {
        if (!values.id) throw new Error('Customer ID is missing');
        await handleUpdateCustomer(values);
        showSnackbar('Success', 'Customer updated successfully', 'success');
      }

      if (onClose) {
        onClose();
      } else if (!stayOnTabAfterEditEnabled || (isNew && !redirectToDetailsPage)) {
        navigate(-1);
      }
      resetForm();
    } catch (error: unknown) {
      showErrorWithDetails(error, 'Failed to save customer', 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  return { handleSubmit, createError, updateError, updateLoading };
}
